import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cabine } from "../componentes/FerramentaUi";
import {
  Bloco,
  CAMPO_CLARO,
  CAMPO_NAVY,
  CampoMoeda,
  GovForaDoAr,
  NumeroHeroi,
  RecusaDoGov,
  Rotulo,
  SeloSimulado,
} from "../componentes/CalculadorasUi";
import { chamar } from "../lib/calculadoras";
import {
  baseEsperada,
  COMPONENTES_CBS_IBS,
  COMPONENTES_IS,
  corpoDaBase,
  emReais,
  numeroDoGov,
  type Componente,
} from "../lib/montarCalculo";
import { rolarAte } from "../lib/rolarPara";
import { BASE } from "./Calculadoras";

// A calculadora das bases de cálculo.
//
// ---------------------------------------------------------------------------
// Duas bases, e elas não são a mesma conta
// ---------------------------------------------------------------------------
// A base do **IBS/CBS** e a base do **Imposto Seletivo** têm listas de
// componentes diferentes, e a diferença é o ponto da aula: o IS não desconta o
// próprio IS (ele é o tributo sendo calculado), tem bonificação e devolução de
// vendas, e não tem PIS/COFINS. Já a base do IBS/CBS SOMA o Imposto Seletivo —
// e é por isso que, no Regime Geral, a base sai maior que o valor da mercadoria.
//
// Por isso as duas aparecem como ABAS da mesma calculadora, e não como duas
// ferramentas: o que se aprende aqui é a comparação.
//
// ---------------------------------------------------------------------------
// Por que a tela soma junto com a Receita
// ---------------------------------------------------------------------------
// Ao lado do número do gov aparece a soma dos componentes que estão na tela,
// com o sinal de cada um. Isso NÃO é uma segunda definição da base: é a
// conferência que o aluno faria no papel, e ela existe porque o valor sozinho
// não ensina nada — o que ensina é ver qual parcela entrou e qual saiu.
//
// Quando os dois números divergem, a tela DIZ. Divergência aqui é informação
// real: significa que o gov aplicou uma regra de vigência que a soma ingênua
// não conhece (o aviso da base unificada mostra que ele ignora campo fora de
// contexto), e o aluno precisa saber disso — esconder faria a soma da tela
// parecer a verdade.

type Aba = "cbs-ibs" | "is";

type Estado =
  | { fase: "vazio" }
  | { fase: "calculando" }
  | { fase: "pronto"; base: number | null; esperado: number; simulado: number | null; aviso: string | null; guardado: boolean; motivoDoRegistro: string | null }
  | { fase: "recusa"; titulo: string; detalhe: string }
  | { fase: "indisponivel"; motivo: string };

const ANO_PADRAO = new Date().getFullYear();

export default function CalcBaseCalculo() {
  const [aba, setAba] = useState<Aba>("cbs-ibs");
  const [ano, setAno] = useState(ANO_PADRAO);
  const [valores, setValores] = useState<Record<string, number | null>>({});
  const [rotulo, setRotulo] = useState("");
  const [estado, setEstado] = useState<Estado>({ fase: "vazio" });
  const resultadoRef = useRef<HTMLDivElement>(null);

  const componentes: Componente[] = aba === "cbs-ibs" ? COMPONENTES_CBS_IBS : COMPONENTES_IS;
  const somam = componentes.filter((c) => c.soma);
  const subtraem = componentes.filter((c) => !c.soma);

  const preenchidos = componentes.filter((c) => valores[c.campo] !== null && valores[c.campo] !== undefined);
  const esperado = baseEsperada(valores, componentes);
  const falta: string[] = [];
  if (!Number.isInteger(ano) || ano < 2026 || ano > 2100) falta.push("o ano do fato gerador");
  if (preenchidos.length === 0) falta.push("pelo menos um componente");

  function trocarAba(nova: Aba) {
    if (nova === aba) return;
    // Os campos são LIMPOS ao trocar de aba, e isto é decisão: os dois
    // conjuntos têm nomes diferentes para a mesma coisa (`frete` no CIBS,
    // `freteCobrado` no IS) e campos que só existem de um lado. Carregar os
    // valores de um para o outro levaria o aluno a achar que informou o ICMS
    // quando não informou.
    setAba(nova);
    setValores({});
    setEstado({ fase: "vazio" });
  }

  async function calcular() {
    if (falta.length > 0) return;
    setEstado({ fase: "calculando" });
    const rota = aba === "cbs-ibs" ? "base-cbs-ibs" : "base-is";
    const corpo = corpoDaBase(ano, valores, componentes);
    const r = await chamar<{ baseCalculo?: unknown }>(rota, {
      corpo,
      guardar: { calculadora: "base-calculo", rotulo: rotulo.trim() || null },
    });

    if (r.situacao === "ok") {
      setEstado({
        fase: "pronto",
        base: numeroDoGov(r.dados?.baseCalculo),
        esperado,
        simulado: r.simulado,
        aviso: r.aviso,
        guardado: r.guardado,
        motivoDoRegistro: r.motivoDoRegistro,
      });
    } else if (r.situacao === "recusa") {
      setEstado({ fase: "recusa", titulo: r.titulo, detalhe: r.detalhe });
    } else {
      setEstado({ fase: "indisponivel", motivo: "motivo" in r ? r.motivo : "falha" });
    }
    requestAnimationFrame(() => rolarAte(resultadoRef.current));
  }

  return (
    <>
      <Cabine
        trilha={{ para: `${BASE}${window.location.search}`, rotulo: "Calculadoras" }}
        rotulo="Calculadora"
        titulo="Base de Cálculo"
        descricao="O que entra e o que sai da base, componente por componente. A Receita responde a base; a tela mostra a sua conta ao lado, para você conferir onde os dois se encontram."
        rodape={
          <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
            <div>
              <Rotulo navy para="bc-ano">
                Ano do fato gerador
              </Rotulo>
              <input
                id="bc-ano"
                type="number"
                min={2026}
                max={2100}
                className={`${CAMPO_NAVY} tabular-nums`}
                value={ano}
                onChange={(e) => setAno(Number(e.target.value))}
              />
            </div>
            <div>
              <Rotulo navy>Qual base</Rotulo>
              {/* As abas são botões e não sub-rota: ao contrário das abas do
                  HUB DP, aqui trocar de aba LIMPA os campos, então não há
                  estado para compartilhar por link — o que se manda ao colega é
                  a calculadora, não um formulário vazio de outra aba. */}
              <div className="flex gap-2" role="tablist">
                {(
                  [
                    { id: "cbs-ibs" as const, nome: "IBS e CBS" },
                    { id: "is" as const, nome: "Imposto Seletivo" },
                  ]
                ).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={aba === t.id}
                    className={
                      // A aba escolhida tem fundo BRANCO, então a tinta dela é
                      // `azul` — cor de marca, escura, 14,57 : 1. Era
                      // `text-navy-ink`, e esse token é o oposto do que o nome
                      // sugere a quem lê depressa: `#f1f1f2` é a tinta CLARA
                      // para escrever SOBRE o navy, não a cor do navy. Sobre
                      // branco ele dá **1,14 : 1** — o rótulo da aba
                      // selecionada sumia, e quem viu foi Victor na tela.
                      //
                      // O precedente é o `SimuladorMeiUi` (`bg-white text-azul
                      // ring-4 ring-laranja/40`): mesmo papel, mesma solução. E
                      // aqui cor FIXA é o certo — dentro do território navy,
                      // cor semântica é a armadilha central desta casa.
                      aba === t.id
                        ? "h-12 rounded-xl bg-white px-5 text-sm font-bold text-azul"
                        : "h-12 rounded-xl border border-navy-mute/40 px-5 text-sm font-semibold text-white transition hover:border-laranja"
                    }
                    onClick={() => trocarAba(t.id)}
                  >
                    {t.nome}
                  </button>
                ))}
              </div>
            </div>
          </div>
        }
      />

      <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_22rem]">
        <div className="flex flex-col gap-5">
          <Bloco
            titulo="O que SOMA na base"
            descricao="Deixe em branco o que não existe na operação. Campo vazio não é zero: informar zero afirmaria à Receita que aquele valor é zero, e não que ele não se aplica."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {somam.map((c) => (
                <div key={c.campo}>
                  <Rotulo para={`bc-${c.campo}`}>{c.rotulo}</Rotulo>
                  <CampoMoeda
                    id={`bc-${c.campo}`}
                    valor={valores[c.campo] ?? null}
                    aoMudar={(n) => setValores((v) => ({ ...v, [c.campo]: n }))}
                  />
                </div>
              ))}
            </div>
          </Bloco>

          <Bloco
            titulo="O que SAI da base"
            descricao={
              aba === "cbs-ibs"
                ? "Os tributos que não compõem a base do IBS e da CBS, e o desconto incondicional."
                : "Os tributos que não compõem a base do Imposto Seletivo, mais bonificações e devoluções. O próprio IS não aparece aqui — ele é o tributo que está sendo calculado."
            }
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {subtraem.map((c) => (
                <div key={c.campo}>
                  <Rotulo para={`bc-${c.campo}`}>{c.rotulo}</Rotulo>
                  <CampoMoeda
                    id={`bc-${c.campo}`}
                    valor={valores[c.campo] ?? null}
                    aoMudar={(n) => setValores((v) => ({ ...v, [c.campo]: n }))}
                  />
                </div>
              ))}
            </div>
          </Bloco>
        </div>

        <div className="lg:sticky lg:top-24 lg:self-start" ref={resultadoRef}>
          <div className="rounded-cf-lg border border-borda bg-superficie p-5">
            <h2 className="font-display text-base font-bold text-titulo">Resultado</h2>

            {/* A conta da tela aparece ANTES de calcular: é o que transforma o
                formulário em exercício. O aluno vê a soma se mover enquanto
                preenche, e só então pede o número oficial. */}
            <div className="mt-3 rounded-xl bg-superficie-2 p-3">
              <p className="text-xs font-semibold text-cinza">A sua conta</p>
              <p data-minha-conta className="mt-0.5 text-lg font-bold text-tinta tabular-nums">
                {preenchidos.length === 0 ? "—" : emReais(esperado)}
              </p>
              <p className="mt-0.5 text-xs text-cinza">
                {preenchidos.length === 0
                  ? "Preencha os componentes ao lado."
                  : `${preenchidos.length} ${preenchidos.length === 1 ? "componente informado" : "componentes informados"}.`}
              </p>
            </div>

            <div className="mt-3">
              <Rotulo para="bc-rotulo">Nome deste cálculo (opcional)</Rotulo>
              <input
                id="bc-rotulo"
                className={CAMPO_CLARO}
                placeholder="Nota 1234 do cliente Z"
                maxLength={120}
                value={rotulo}
                onChange={(e) => setRotulo(e.target.value)}
              />
            </div>

            <button
              type="button"
              className="pill-cta mt-4 w-full disabled:opacity-50"
              disabled={falta.length > 0 || estado.fase === "calculando"}
              onClick={() => void calcular()}
            >
              {estado.fase === "calculando" ? "Calculando…" : "Calcular na Receita"}
            </button>

            {falta.length > 0 && (
              <p className="mt-2 text-xs leading-relaxed text-cinza">
                Falta preencher: {falta.join(", ")}.
              </p>
            )}

            <div className="mt-4 flex flex-col gap-4">
              {estado.fase === "recusa" && (
                <RecusaDoGov titulo={estado.titulo} detalhe={estado.detalhe} />
              )}
              {estado.fase === "indisponivel" && <GovForaDoAr motivo={estado.motivo} />}
              {estado.fase === "pronto" && (
                <>
                  <SeloSimulado codigo={estado.simulado} texto={estado.aviso} />
                  <NumeroHeroi
                    rotulo={aba === "cbs-ibs" ? "Base do IBS e da CBS" : "Base do Imposto Seletivo"}
                    valor={emReais(estado.base)}
                    apoio="Calculada pela Receita Federal."
                  />
                  {estado.base !== null && Math.abs(estado.base - estado.esperado) >= 0.01 && (
                    <div data-divergencia className="rounded-xl border border-alerta-tinta/40 bg-alerta-fundo p-3">
                      <p className="text-xs leading-relaxed font-semibold text-alerta-tinta">
                        A sua conta deu {emReais(estado.esperado)} e a da Receita deu{" "}
                        {emReais(estado.base)}.
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-alerta-tinta">
                        A diferença costuma ser de vigência: alguns componentes só compõem a
                        base em certos anos (PIS e COFINS em 2026, ICMS até 2032). O número da
                        Receita é o que vale.
                      </p>
                    </div>
                  )}
                  {!estado.guardado && (
                    <p className="text-xs leading-relaxed text-cinza">
                      {estado.motivoDoRegistro === "historico-cheio"
                        ? "O seu histórico está cheio (500 cálculos). Este resultado não foi guardado — apague alguns na página inicial da ferramenta."
                        : "Este cálculo não foi guardado no seu histórico, mas o resultado acima é válido."}
                    </p>
                  )}
                </>
              )}
              {estado.fase === "vazio" && preenchidos.length > 0 && (
                <p className="text-sm leading-relaxed text-cinza">
                  Clique em calcular para comparar a sua conta com a da Receita.
                </p>
              )}
            </div>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-cinza">
            <Link to={`${BASE}${window.location.search}`} className="text-link hover:underline">
              Voltar às três calculadoras
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
